import { trackError } from '@billy/track-error'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { Scope } from '../../../enums/scope'
import { isAuthorized } from '../../../utils/isAuthorized'
import { PDP_NETWORK_TYPE } from '../constants'
import { STEP_COUNT_MAP } from '../constants/stepCount'
import { STEP_TRANSITIONS_FORWARD } from '../constants/stepTransitions'
import { useEInvoicingSetupContext } from '../context/EInvoicingSetupContext'
import { EInvoicingSetupStep, KYCDocument } from '../types'
import { navigateToUploadsUrl } from '../utils'
import { useGetEInvoicingIntegrationPdp } from './useGetEInvoicingIntegrationPdp'
import { useGetEInvoicingKYCStatus } from './useGetEInvoicingKYCStatus'

export const useEInvoicingSetup = () => {
  const navigate = useNavigate()

  const { currentStep, transitionToStep, ...context } = useEInvoicingSetupContext()
  const { organization } = useUserOrganization()
  const { data, isProcessing: isLoadingKYC } = useGetEInvoicingKYCStatus()
  const {
    eInvoiceIntegration,
    eInvoiceIntegrationPdpActivationDate,
    isProcessing: isLoadingIntegration,
  } = useGetEInvoicingIntegrationPdp()

  const isUserAuthorizedForKYC = isAuthorized(Scope.EDocumentRecieveRegistration)

  const currentStepCount = STEP_COUNT_MAP[currentStep.current]
  const isEInvoicingEnabled = useMemo(() => organization?.countryId === 'FR', [organization?.countryId])

  const kycUnderReview =
    data?.status === 'KYC_PENDING_COMPANY_ID' ||
    data?.status === 'KYC_PENDING_REVIEW_CREATION' ||
    data?.status === 'KYC_PENDING_VALIDATION'

  const kycStatus = useMemo(() => {
    // In the case of no KYC attempt: prompt user to Pending Reception if coming from Cegid Console, or Get Started otherwise
    if (!data?.status) {
      return 'KYC_PENDING_DOCUMENTS'
    }

    return data.status
  }, [data?.status])

  const kycCerfaDocument = useMemo<KYCDocument | null>(
    () => data?.kycDocuments.find((doc) => doc.type === 'CERFA') || null,
    [data?.kycDocuments],
  )

  const isEInvoicingActivated = kycStatus === 'KYC_APPROVED' && eInvoiceIntegration?.status === 'ACTIVE'
  const isReactivationInPendingReview =
    eInvoiceIntegration?.status === 'INACTIVE' && kycStatus === 'KYC_PENDING_DOCUMENTS'

  const isForwardTransitionAllowed = useCallback(
    (step: EInvoicingSetupStep) => {
      const allowedTransitions = STEP_TRANSITIONS_FORWARD[currentStep.current]
      return allowedTransitions.includes(step) || currentStep.current === step
    },
    [currentStep],
  )

  const goToStep = useCallback(
    (step: EInvoicingSetupStep) => {
      // Check if step is allowed
      if (!isForwardTransitionAllowed(step)) {
        trackError(`Transition ${currentStep} → ${step} is not allowed`)
        return
      }

      // Navigate to step
      transitionToStep(step)
    },
    [currentStep, transitionToStep, isForwardTransitionAllowed],
  )

  const goToNextStep = useCallback(() => {
    const allowedTransitions = STEP_TRANSITIONS_FORWARD[currentStep.current]
    if (allowedTransitions.length > 1) {
      trackError(`Transition from ${currentStep} has multiple steps, cannot select only one`)
      return
    }
    queryClient.invalidateQueries([QueryKeys.EInvoicingGetKYCStatus, PDP_NETWORK_TYPE, organization?.id])
    transitionToStep(allowedTransitions[0])
  }, [currentStep, organization?.id, transitionToStep])

  const restartFlow = useCallback(() => {
    transitionToStep('EMPTY_STATE')
  }, [transitionToStep])

  const navigateToEInvoices = useCallback(() => {
    if (organization) {
      navigateToUploadsUrl(organization, navigate)
    }
  }, [navigate, organization])

  return {
    navigateToEInvoices,
    currentStep: currentStep.current,
    currentStepCount,
    goToStep,
    goToNextStep,
    isEInvoicingEnabled,
    isUserAuthorizedForKYC,
    kycStatus,
    kycDocuments: data?.kycDocuments,
    kycCerfaDocument,
    kycUnderReview,
    legalUnitEstablishment: data?.legalUnitEstablishment,
    eInvoiceIntegration,
    eInvoiceIntegrationPdpActivationDate,
    isEInvoicingActivated,
    isReactivationInPendingReview,
    restartFlow,
    loading: isLoadingKYC || isLoadingIntegration,
    ...context,
  }
}
